import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/about.scss";

const principles = [
  {
    number: "01",
    title: "Material first",
    copy: "We list the composition of every item so material choices are easy to understand.",
  },
  {
    number: "02",
    title: "Designed to stay",
    copy: "Clean proportions and considered colour help each piece work across an everyday wardrobe.",
  },
  {
    number: "03",
    title: "Information clearly",
    copy: "Fit, composition, care, delivery and returns are presented without unsupported certification claims.",
  },
];

const materials = [
  ["Cotton", "A familiar fibre used across jersey, twill, denim and knit constructions."],
  ["Linen", "A crisp, textured fibre featured in warm-weather shirts and lightweight layers."],
  ["Wool", "A warm fibre used in fine knits and softly structured tailoring."],
  ["Lyocell", "A smooth cellulosic fibre used for fluid tops and softly draped blouses."],
];

const milestones = [
  {
    year: "Origin",
    title: "A useful wardrobe",
    copy: "Filverparcel begins with a simple idea: make everyday pieces that travel easily through work, rest and changing seasons.",
  },
  {
    year: "First edit",
    title: "Fewer, clearer choices",
    copy: "The first edit brings together natural textures, calm colour and straightforward silhouettes for women and men.",
  },
  {
    year: "Online",
    title: "The parcel sets off",
    copy: "Our online store makes each piece easy to discover, compare and send wherever the next day leads.",
  },
  {
    year: "Today",
    title: "The standard keeps moving",
    copy: "We continue to refine product information and make the collection easier to navigate.",
  },
];

function About() {
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <main className="about info-page" id="main-content">
      <div className="info-shell">
        <nav className="info-breadcrumb" aria-label="Breadcrumb">
          <Link to="/" onClick={scrollToTop}>
            Home
          </Link>
          <span aria-hidden="true">/</span>
          <span aria-current="page">Brand mission</span>
        </nav>

        <header className="about-hero info-page__hero">
          <div className="about-hero__meta">
            <p className="info-page__eyebrow">{siteConfig.brandName}</p>
            <p className="about-hero__tagline">{siteConfig.tagline}</p>
          </div>
          <div className="about-hero__content">
            <h1>Good design begins long before the garment.</h1>
            <p>
              We present considered clothing for real life—modern in attitude,
              easy to compare and supported by clear product information.
            </p>
          </div>
        </header>

        <figure className="about-editorial">
          <img
            src="/main/about-atelier.jpg"
            alt="A designer arranging natural fabric and garment patterns in a quiet studio"
            loading="lazy"
          />
        </figure>

        <section className="about-statement" aria-labelledby="about-mission-title">
          <p className="info-page__eyebrow">Our mission</p>
          <div>
            <h2 id="about-mission-title">Clarity from the first look.</h2>
            <p>
              At {siteConfig.brandName}, composition, fit, available sizes,
              colours and care information are brought together so every item
              can be considered on its own terms.
            </p>
            <p>
              The result is a quiet, metropolitan wardrobe that avoids loud
              branding and short-lived novelty. Each piece is intended to feel
              current now and convincing years from now.
            </p>
          </div>
        </section>

        <section className="about-principles" aria-labelledby="about-principles-title">
          <div className="about-section-heading">
            <p className="info-page__eyebrow">How we work</p>
            <h2 id="about-principles-title">One garment. Clear information.</h2>
          </div>
          <div className="about-principles__grid">
            {principles.map((principle) => (
              <article className="about-principle" key={principle.number}>
                <span>{principle.number}</span>
                <h3>{principle.title}</h3>
                <p>{principle.copy}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="about-materials" aria-labelledby="about-materials-title">
          <div className="about-materials__intro">
            <p className="info-page__eyebrow">Materials</p>
            <h2 id="about-materials-title">Texture at a glance.</h2>
            <p>
              Product pages state the supplied fibre composition. These simple
              notes describe how the materials appear across the collection;
              they are not certification or sourcing claims.
            </p>
          </div>
          <dl className="about-materials__list">
            {materials.map(([name, description], index) => (
              <div key={name}>
                <dt>
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  {name}
                </dt>
                <dd>{description}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section className="about-timeline" aria-labelledby="about-timeline-title">
          <div className="about-section-heading">
            <p className="info-page__eyebrow">Our path</p>
            <h2 id="about-timeline-title">Built as an ongoing practice.</h2>
          </div>
          <ol className="about-timeline__list">
            {milestones.map((milestone) => (
              <li key={milestone.year}>
                <span className="about-timeline__year">{milestone.year}</span>
                <div>
                  <h3>{milestone.title}</h3>
                  <p>{milestone.copy}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className="about-cta" aria-label="Explore the collection">
          <p className="info-page__eyebrow">Wear the idea</p>
          <h2>Clothes are most meaningful when they become part of your life.</h2>
          <div className="info-page__actions">
            <Link className="info-button info-button--light" to="/shop" onClick={scrollToTop}>
              Explore the collection
            </Link>
            <Link className="info-text-link info-text-link--light" to="/contact" onClick={scrollToTop}>
              Talk to our team
            </Link>
          </div>
        </section>
      </div>
    </main>
  );
}

export default About;
